import { PageHeader } from "@/components/page-header";
import { QueryErrorCard } from "@/components/query-error-card";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { useActivity, useMeDetail } from "@/lib/api";

import { ProfileForm } from "./profile-form";

export function ProfilePage() {
  const profileQuery = useMeDetail();
  const activityQuery = useActivity();

  const profile = profileQuery.data;
  const activity = activityQuery.data ?? [];
  const isLoading = profileQuery.isLoading;
  const hasError = profileQuery.isError || activityQuery.isError;

  const retry = () => {
    void profileQuery.refetch();
    void activityQuery.refetch();
  };

  return (
    <div className="mx-auto w-full max-w-6xl px-4 py-6 sm:px-6">
      <PageHeader
        eyebrow="Your account"
        title="Profile"
        description="Review your account details, update your name, and see your recent activity on the platform."
      />

      <div className="max-w-2xl space-y-6">
        {isLoading ? (
          <>
            <Skeleton className="h-40 w-full rounded-lg" />
            <Skeleton className="h-44 w-full rounded-lg" />
          </>
        ) : hasError || !profile ? (
          <QueryErrorCard
            message="Could not load your profile data."
            onRetry={retry}
            backTo="/home"
          />
        ) : (
          <>
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Account info</CardTitle>
              </CardHeader>
              <CardContent>
                <dl className="grid grid-cols-[7rem_1fr] gap-y-2 text-sm">
                  <dt className="text-muted">Email</dt>
                  <dd className="text-foreground">{profile.email}</dd>
                  <dt className="text-muted">Role</dt>
                  <dd className="text-foreground">{profile.role}</dd>
                  <dt className="text-muted">Member since</dt>
                  <dd className="text-foreground">
                    {new Date(profile.created_at).toLocaleDateString()}
                  </dd>
                </dl>
              </CardContent>
            </Card>

            <ProfileForm
              initialFirstName={profile.first_name ?? ""}
              initialLastName={profile.last_name ?? ""}
            />

            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Activity log</CardTitle>
              </CardHeader>
              <CardContent>
                {activity.length === 0 ? (
                  <p className="text-muted text-sm">No activity yet.</p>
                ) : (
                  <div className="divide-border divide-y">
                    {activity.map((entry) => (
                      <div
                        key={entry.id}
                        className="flex items-start justify-between gap-4 py-2.5 first:pt-0 last:pb-0"
                      >
                        <div className="min-w-0">
                          <p className="text-foreground text-sm font-medium">{entry.action}</p>
                          {entry.detail && <p className="text-muted text-xs">{entry.detail}</p>}
                        </div>
                        <p className="text-muted text-xs whitespace-nowrap">
                          {new Date(entry.created_at).toLocaleString()}
                        </p>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </>
        )}
      </div>
    </div>
  );
}
